import { X } from 'lucide-react';
import { Dialog as SheetPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn.ts';

/**
 * shadcn's sheet: a panel that slides in from an edge over a dimmed page,
 * focus held inside, Escape or the close button to leave. The only surface
 * with a shadow. `right` for a record's details, `left` for the menu drawer.
 * Its header holds the title (what a screen reader names it by) and the
 * close button; the body under it scrolls.
 */
export const UiSheet = SheetPrimitive.Root;

const SIDE = {
  right: 'inset-y-0 right-0 w-full max-w-sheet tablet:border-l data-[state=closed]:animate-sheet-out data-[state=open]:animate-sheet-in',
  left: 'inset-y-0 left-0 w-full max-w-sidebar border-r data-[state=closed]:animate-drawer-out data-[state=open]:animate-drawer-in',
} as const;

export const closeButtonClass =
  'inline-flex size-control shrink-0 cursor-pointer items-center justify-center rounded-control text-ink-2 hover:bg-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-brand [&_svg]:size-icon';

export function UiSheetContent({
  side = 'right',
  title,
  closeLabel,
  children,
  ...props
}: Omit<ComponentProps<typeof SheetPrimitive.Content>, 'title' | 'className'> & { readonly side?: keyof typeof SIDE; readonly title: ReactNode; readonly closeLabel: string }) {
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay data-slot="sheet-overlay" className="fixed inset-0 z-40 bg-overlay data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in motion-reduce:animate-none" />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        aria-describedby={undefined}
        // The sheet itself takes focus, not its close button: a ring on the corner reads as a mistake to a mouse user; Tab reaches the button next.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
        className={cn('fixed z-50 flex flex-col border-line bg-surface font-sans text-body text-ink shadow-float outline-none motion-reduce:animate-none', SIDE[side])}
        {...props}
      >
        <div className="flex min-h-topbar shrink-0 items-center justify-between gap-s3 border-b border-line py-s2 pr-s3 pl-s5">
          <SheetPrimitive.Title asChild>
            <div className="flex min-w-0 items-center gap-s3">{title}</div>
          </SheetPrimitive.Title>
          <SheetPrimitive.Close aria-label={closeLabel} className={closeButtonClass}>
            <X aria-hidden="true" />
          </SheetPrimitive.Close>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">{children}</div>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}
